import {
  Text,
  Link,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Util } from '@finley-technologies/finley-types';

const getErrorCode = (errorMessage?: string): string | null => {
  if (!errorMessage) return null;

  const errorCode = errorMessage.match(/(?<=\[errorCode:)(.*?)(?=\])/);
  return errorCode ? errorCode[0] : null;
};

const getErrorMessageBody = (
  errorCode?: Util.Maybe<string>,
): React.ReactNode => {
  switch (errorCode) {
    case 'bankAccountsAreNotConfiguredProperly':
      return (
        <Text>
          Configure credit facility bank accounts in{' '}
          <Link
            as={RouterLink}
            to="/settings/bank-accounts"
            style={{ textDecoration: 'underline' }}
          >
            Settings
          </Link>
          .
        </Text>
      );
    case 'classAllocationMethodNotConfigured':
      return (
        <Text>
          Contact support to configure class allocation method before
          continuing.
        </Text>
      );
    case 'collectionsPaydownIsAllowedNotConfigured':
      return (
        <Text>
          Contact support to configure collections paydown before continuing.
        </Text>
      );
    case 'collectionsRecycleIsAllowedNotConfigured':
      return (
        <Text>
          Contact support to configure collections recycle before continuing.
        </Text>
      );
    case 'currentFundingRequestAlreadyInProgress':
      return <Text>A funding request is currently in progress.</Text>;
    case 'expireDaysOffsetNotConfigured':
      return (
        <Text>
          Contact support to configure funding request expiration days offset
          before continuing.
        </Text>
      );
    case 'fundingRequestComponentSubmissionNoticePeriodsNotConfigured':
      return (
        <Text>
          Contact support to configure submission notice periods before
          continuing.
        </Text>
      );
    case 'isStopDrawEnforcedNotConfigured':
      return (
        <Text>
          Contact support to configure stop draw for insufficient collections
          cash parameter before continuing.
        </Text>
      );
    case 'latestBorrowingBaseReportIsTooOld':
      return (
        <Text>
          The latest borrowing base report is too old to be used. Deliver new
          tapes via existing workflow or on the{' '}
          <Button variant="link" as={RouterLink} to="/data-pipeline">
            Data Pipeline
          </Button>{' '}
          page.
        </Text>
      );
    case 'maximumBorrowingBaseReportDaysOldNotConfigured':
      return (
        <Text>
          Contact support to configure borrowing base report expiration days
          limit before continuing.
        </Text>
      );
    case 'noCurrentFundingRequestConstraint':
      return <Text>Create a new borrowing base report to continue.</Text>;
    case 'servicerReportTemplateNotConfigured':
      return (
        <Text>
          Contact support to configure servicer report before continuing.
        </Text>
      );
    default:
      return (
        <Text>
          An unexpected error occurred. Please contact Finley for support.
        </Text>
      );
  }
};

type FundingRequestErrorAlertProps = {
  errorMessage?: string;
};

export const FundingRequestErrorAlert = ({
  errorMessage,
}: FundingRequestErrorAlertProps) => {
  const errorCode = getErrorCode(errorMessage);
  return (
    <Alert status="error">
      <AlertIcon />
      <AlertDescription>{getErrorMessageBody(errorCode)}</AlertDescription>
    </Alert>
  );
};
